import memoize from 'memoize-one';
import deepEqual from 'fast-deep-equal';

import type { Auth as MediaAuth, AuthProvider, ClientBasedAuth } from '@atlaskit/media-core';

import {
	mediaConfigEnforceDSPRecorder,
	mediaConfigRecorder,
} from '@confluence/ssr-utilities/entry-points/ObjectRecorder.ts';
import { fg } from '@confluence/feature-gating';

import { createRendererMediaProvider } from './MediaProvider/MediaProvider.ts';

type MediaSSR = {
	mode: 'server' | 'client';
	config: {
		authProvider: AuthProvider;
		initialAuth?: MediaAuth;
		enforceDataSecurityPolicy?: boolean;
	};
};

type AuthProviderConfigOptions = {
	contentAri?: string;
	contentId?: string;
	contentProduct?: 'confluence-page' | 'jira-work-item';
	spaceKey?: string;
	isExternalShare: boolean;
	mediaToken?: string;
};

const MEDIA_PROVIDER_UNAVAILABLE_ERROR = 'mediaProvider unavailable, must use initialAuth';

// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
const isServerEnvironment = () => process.env.REACT_SSR;

const isClientEnvironment = () => window.__SSR_RENDERED__;

const getInitialAuth = (config: AuthProviderConfigOptions): ClientBasedAuth | undefined => {
	const recordedAuth = mediaConfigRecorder?.get();
	if (!recordedAuth) return undefined;
	return {
		...recordedAuth,
		// If the mediaToken is provided in the config, use it.
		// This ensures the correct token is used in cases where the token differs from
		// the initial page, such as media included through an `include` macro.
		token: config?.mediaToken || recordedAuth.token,
	};
};

const getInitialEnforceDataSecurityPolicy = (): boolean => {
	if (!fg('dlp_attachment_download_respects_data_export_dsp')) return false;
	const shouldEnforceDataSecurityPolicy = mediaConfigEnforceDSPRecorder?.get();
	return !!shouldEnforceDataSecurityPolicy;
};

const getServerModeConfig = (config: AuthProviderConfigOptions): MediaSSR => ({
	mode: 'server' as const,
	config: {
		authProvider: () => Promise.reject(new Error(MEDIA_PROVIDER_UNAVAILABLE_ERROR)),
		initialAuth: getInitialAuth(config),
		enforceDataSecurityPolicy: getInitialEnforceDataSecurityPolicy(),
	},
});

const createClientAuthProvider =
	({
		contentAri,
		contentId,
		contentProduct,
		spaceKey,
		isExternalShare,
		mediaToken,
	}: AuthProviderConfigOptions): AuthProvider =>
	async (authContext): Promise<MediaAuth> => {
		if (!!contentId && (!!spaceKey || contentProduct === 'jira-work-item')) {
			const { viewMediaClientConfig } = await createRendererMediaProvider({
				contentAri,
				contentId,
				contentProduct,
				spaceKey,
				isExternalShare,
				mediaToken,
			});
			return viewMediaClientConfig.authProvider(authContext);
		} else {
			throw new Error(MEDIA_PROVIDER_UNAVAILABLE_ERROR);
		}
	};

const getClientModeConfig = (config: AuthProviderConfigOptions): MediaSSR => ({
	mode: 'client' as const,
	config: {
		authProvider: createClientAuthProvider(config),
		initialAuth: window.__MEDIA_CONFIG__ as MediaAuth,
		enforceDataSecurityPolicy: window.__MEDIA_CONFIG_ENFORCE_DSP__ as boolean,
	},
});

const getMediaSSRModeAndAuthConfig = (config: AuthProviderConfigOptions): MediaSSR | undefined => {
	if (isServerEnvironment()) {
		return getServerModeConfig(config);
	} else if (isClientEnvironment()) {
		return getClientModeConfig(config);
	}
};

export const getMediaSSRModeAndAuthConfigMemoized = memoize(
	getMediaSSRModeAndAuthConfig,
	deepEqual,
);

export const useMediaSSRModeAndAuthConfig = (
	config: AuthProviderConfigOptions,
): MediaSSR | undefined => {
	return getMediaSSRModeAndAuthConfigMemoized(config);
};
